import React, { useState } from 'react';
import { MessageSquare, Mail, Phone, MapPin, Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { companies } from '../data/mockData';
import { useToast } from '../hooks/use-toast';
import SEOHead from '../components/SEOHead';

const Contact = () => {
  const { toast } = useToast();
  const holding = companies.holding;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Scroll to top on mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Netlify Forms submission
      const form = e.target;
      const formDataEncoded = new URLSearchParams(new FormData(form)).toString();
      
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataEncoded,
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        toast({
          title: "Anfrage erfolgreich versendet!",
          description: "Wir melden uns in Kürze bei Ihnen.",
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('Fehler beim Senden');
      }
      
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Fehler",
        description: "Ihre Anfrage konnte nicht gesendet werden. Bitte kontaktieren Sie uns direkt per Telefon oder E-Mail.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen pt-20">
      <SEOHead 
        title="Kontakt | immoriya Projektentwicklung GmbH"
        description="Kontaktieren Sie immoriya in Osnabrück. Wir beraten Sie zu Projektentwicklung, Sanierung, Immobilienverkauf und Vermietung. Telefon: +49 172 560 66 23"
        canonical="https://immoriya.de/kontakt"
      />

      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Kontakt – Ihr Projekt ist unser Antrieb
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              Lassen Sie uns über Ihr Vorhaben sprechen. Ob Sanierung, Verkauf, Projektentwicklung oder Vermietung – wir finden die passende Lösung.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-gray-200">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Ihre Anfrage
                  </h2>
                  
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Vielen Dank!</h3>
                      <p className="text-gray-600">Ihre Anfrage wurde erfolgreich versendet. Wir melden uns in Kürze bei Ihnen.</p>
                      <Button 
                        onClick={() => setIsSubmitted(false)} 
                        variant="outline" 
                        className="mt-6"
                      >
                        Neue Anfrage senden
                      </Button>
                    </div>
                  ) : (
                  <form 
                    name="kontakt" 
                    method="POST" 
                    data-netlify="true"
                    netlify-honeypot="bot-field"
                    onSubmit={handleSubmit} 
                    className="space-y-6"
                  >
                    <input type="hidden" name="form-name" value="kontakt" />
                    <p className="hidden">
                      <label>Don't fill this out: <input name="bot-field" /></label>
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                          Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full"
                          placeholder="Ihr vollständiger Name"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                          E-Mail *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full"
                          placeholder="ihre@email.de"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                          Telefon
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full"
                          placeholder="+49 XXX XXXXXXX"
                        />
                      </div>
                      <div>
                        <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 mb-2">
                          Betreff *
                        </label>
                        <Select 
                          value={formData.subject}
                          onValueChange={(value) => setFormData({...formData, subject: value})}
                          required
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Bitte wählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sanierung">Sanierung/Ausbau</SelectItem>
                            <SelectItem value="projektentwicklung">Projektentwicklung</SelectItem>
                            <SelectItem value="verkauf">Immobilie verkaufen</SelectItem>
                            <SelectItem value="partnerschaft">Partnerschaft/Investition</SelectItem>
                            <SelectItem value="monteurvermietung">Monteurvermietung</SelectItem>
                            <SelectItem value="sonstiges">Sonstiges</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                        Ihre Nachricht *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        rows={6}
                        className="w-full"
                        placeholder="Beschreiben Sie bitte Ihr Anliegen..."
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          Nachricht senden
                          <Send className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-gray-500 text-center">
                      * Pflichtfelder
                    </p>
                  </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-gray-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Direkter Kontakt
                  </h3>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 mb-1">Telefon & WhatsApp</div>
                        <a href={`tel:${holding.contact.phone}`} className="text-blue-600 hover:text-blue-700 block mb-2">
                          {holding.contact.phone}
                        </a>
                        <a 
                          href={`https://wa.me/${holding.contact.whatsapp.replace(/\s/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          WhatsApp
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 mb-1">E-Mail</div>
                        <a href={`mailto:${holding.contact.email}`} className="text-blue-600 hover:text-blue-700 break-all">
                          {holding.contact.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 mb-1">Adresse</div>
                        <div className="text-gray-600">
                          {holding.address.street}<br />
                          {holding.address.zip} {holding.address.city}<br />
                          {holding.address.country}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Öffnungszeiten
                  </h3>
                  <div className="space-y-2 text-gray-700">
                    <div className="flex justify-between">
                      <span>Montag - Freitag</span>
                      <span className="font-semibold">08:00 - 18:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Samstag</span>
                      <span className="font-semibold">Nach Vereinbarung</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sonntag</span>
                      <span className="font-semibold">Geschlossen</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold mb-4">
                    Energiya Kontakt
                  </h3>
                  <p className="text-white/90 mb-4 leading-relaxed">
                    Für Anfragen zu energetischer Sanierung und Ausbau kontaktieren Sie direkt:
                  </p>
                  <a 
                    href="https://energiya.immoriya.de" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 font-semibold"
                  >
                    energiya.immoriya.de →
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section - Google Maps Embed */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Unser Standort</h2>
            <p className="text-gray-600">Besuchen Sie uns in Osnabrück</p>
          </div>
          <Card className="overflow-hidden border-gray-200">
            <div className="aspect-video w-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2436.5877252784247!2d8.04254!3d52.2799!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b9e5e1c5c5c5c5%3A0x1234567890abcdef!2sDammstra%C3%9Fe%202%2C%2049084%20Osnabr%C3%BCck!5e0!3m2!1sde!2sde!4v1700000000000!5m2!1sde!2sde"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="immoriya Standort - Dammstraße 2, Osnabrück"
                className="w-full h-full"
              />
            </div>
          </Card>
          <div className="mt-6 text-center">
            <a 
              href="https://www.google.com/maps/dir//Dammstra%C3%9Fe+2,+49084+Osnabr%C3%BCck"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Route planen
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
